/** What the job listing says of a job, as far as starting its setup over needs. */
interface ListedJob {
  id?: unknown;
  status?: unknown;
  bootstrapMode?: unknown;
  documentTypes?: unknown;
}

/** The two ways a job is set up from sample documents (App.tsx `usesRepresentativeBootstrap`). */
const fromSamples = new Set(['folder', 'sample-scan']);

/**
 * A first setup from sample documents lives in the tab that runs it (folder-analysis.ts).
 * CapturePoint's desktop keeps it in a file beside the workspace and, after a restart, reads a
 * failure back from it; Paige's tab forgets it on a reload. The workbench forces a provisional job
 * set up from samples into Job setup (App.tsx `representativeSetupRequired`), so a job whose sample
 * failed, was stopped or was lost with a reload opened on an empty Document types editor ("Add your
 * first document type"), with no way back to its sources (production, 2026-09-30, "AP Real 01").
 *
 * Such a job — provisional, set up from samples, no document types, and no setup this tab is
 * reading or still holds the end of — is listed as the job it was before its samples were chosen:
 * one that has not started, whose start screen offers Choose files, Bring in a folder, Paige Scan
 * and Email again. A setup that failed in this tab keeps its failure and "Choose documents again"
 * until the person leaves the job (CapturePointWorkbench `useLeave`), so a remount does not lose
 * it. The stored definition follows on the job's next save, as it does after CapturePoint's own
 * "Start scanning instead" (`continueWithoutFolderLearning`). A sample being read in another tab
 * is not visible from this one; that tab still finishes and applies it.
 */
export function startUnfinishedSetupsOver(
  jobs: unknown,
  setupInHand: (jobId: string) => boolean,
): unknown {
  if (!Array.isArray(jobs)) return jobs;
  return jobs.map((job: unknown) => (unfinished(job, setupInHand) ? notStarted(job) : job));
}

function unfinished(job: unknown, setupInHand: (jobId: string) => boolean): job is ListedJob {
  if (!job || typeof job !== 'object') return false;
  const { id, status, bootstrapMode, documentTypes } = job as ListedJob;
  return (
    typeof id === 'string' &&
    status === 'provisional' &&
    typeof bootstrapMode === 'string' &&
    fromSamples.has(bootstrapMode) &&
    (!Array.isArray(documentTypes) || documentTypes.length === 0) &&
    !setupInHand(id)
  );
}

/** A job not started yet is one set up as its documents come in, the new-job dialog's default. */
function notStarted(job: ListedJob): ListedJob {
  return { ...job, bootstrapMode: 'scan' };
}
